import { AutoComplete as AntdAutoComplete, Input, Spin, Typography } from 'antd';
import type { InputProps, InputRef } from 'antd';
import { BaseRecord } from '@refinedev/core';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { httpClient, ROOT_URL } from '../../../App';
import { dropdownStyles } from './styles';

type SmartUidSuggestionItem = BaseRecord & {
  smartUID: string;
  fullName?: string;
  name?: string;
  email?: string;
  code?: string;
};

export type SmartUidAutoCompleteInputProps = Omit<
  InputProps,
  'value' | 'onChange'
> & {
  value?: string;
  onChange?: (value: string) => void;
  onSuggestionSelect?: (smartUID: string) => void;
};

const SMART_UID_MIN_CHARS = 3;
const SMART_UID_DEBOUNCE_MS = 400;

const parseSmartUidSearchResponse = (response: any): SmartUidSuggestionItem[] => {
  const payload = response?.data;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }

  if (Array.isArray(payload?.data?.items)) {
    return payload.data.items;
  }

  return [];
};

const formatSmartUidSuggestionLabel = (item: SmartUidSuggestionItem) => {
  const secondaryText = item.fullName || item.name || item.email || item.code;

  return secondaryText ? `${item.smartUID} - ${secondaryText}` : item.smartUID;
};

const searchUsersBySmartUidPrefix = (query: string) => {
  const encoded = encodeURIComponent(query.trim());

  return httpClient.get(`${ROOT_URL}/api/v1/users/smart-uid/${encoded}`);
};

export const SmartUidAutoCompleteInput = React.forwardRef<
  InputRef,
  SmartUidAutoCompleteInputProps
>(
  (
    {
      onBlur,
      onSuggestionSelect,
      placeholder,
      size,
      style,
      disabled,
      value,
      onChange,
      className,
      ...inputRest
    },
    ref
  ) => {
    const skipBlurAfterSelectRef = useRef(false);
    const [options, setOptions] = useState<
      { value: string; label: React.ReactNode }[]
    >([]);
    const [loading, setLoading] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const runFetch = useCallback(async (text: string) => {
      const query = text.trim();

      if (query.length < SMART_UID_MIN_CHARS) {
        setLoading(false);
        setOptions([]);
        return;
      }

      setLoading(true);

      try {
        const response = await searchUsersBySmartUidPrefix(query);
        const items = parseSmartUidSearchResponse(response);

        setOptions(
          items
            .filter((item) => typeof item?.smartUID === 'string' && item.smartUID.trim().length > 0)
            .map((item) => ({
              value: item.smartUID,
              label: (
                <Typography.Text>
                  {formatSmartUidSuggestionLabel(item)}
                </Typography.Text>
              ),
            }))
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, []);

    const debouncedFetch = useMemo(
      () => debounce(runFetch, SMART_UID_DEBOUNCE_MS, { trailing: true }),
      [runFetch]
    );

    useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

    const handleSearch = useCallback(
      (text: string) => {
        if (text.trim().length < SMART_UID_MIN_CHARS) {
          debouncedFetch.cancel();
          setLoading(false);
          setOptions([]);
          setIsDropdownOpen(false);
          return;
        }

        setIsDropdownOpen(true);
        setLoading(true);
        debouncedFetch(text);
      },
      [debouncedFetch]
    );

    const inputStyle: React.CSSProperties = {
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      ...style,
    };
    const normalizedValue = String(value || '').trim();
    const canOpenDropdown = normalizedValue.length >= SMART_UID_MIN_CHARS;

    return (
      <div className={className} style={{ width: '100%', maxWidth: '100%' }}>
        <AntdAutoComplete
          value={value}
          options={options}
          filterOption={false}
          onSearch={handleSearch}
          onChange={(nextValue) => onChange?.(String(nextValue || ''))}
          onSelect={(selected) => {
            const smartUID = String(selected).trim();

            skipBlurAfterSelectRef.current = true;
            setIsDropdownOpen(false);
            onChange?.(smartUID);
            onSuggestionSelect?.(smartUID);

            window.setTimeout(() => {
              skipBlurAfterSelectRef.current = false;
            }, 0);
          }}
          disabled={disabled}
          open={!disabled && canOpenDropdown && isDropdownOpen}
          style={{ width: '100%', maxWidth: '100%' }}
          dropdownStyle={{ ...dropdownStyles, padding: 0 }}
          dropdownMatchSelectWidth
          notFoundContent={
            loading ? (
              <div style={{ padding: '10px', textAlign: 'center' }}>
                <Spin size='small' />
              </div>
            ) : (
              <Typography.Text style={{ display: 'block', padding: '10px' }}>
                No suggestions
              </Typography.Text>
            )
          }
        >
          <Input
            ref={ref}
            {...inputRest}
            placeholder={placeholder}
            size={size}
            style={inputStyle}
            disabled={disabled}
            onBlur={(e) => {
              setIsDropdownOpen(false);

              if (skipBlurAfterSelectRef.current) {
                return;
              }

              onBlur?.(e);
            }}
          />
        </AntdAutoComplete>
      </div>
    );
  }
);

SmartUidAutoCompleteInput.displayName = 'SmartUidAutoCompleteInput';
